/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AfterViewInit, Component, EventEmitter, Input, Output } from '@angular/core';
import { SocketService, UtilsService } from '../../services';
import { WidgetBaseComponent } from '../../../modules/loadbalancer/shared/widgets/widgetBase.component';
import { WidgetStateEnum } from '../../enums';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-widget',
    templateUrl: './widget.component.html',
    styleUrls: ['./widget.component.scss']
})

/**
 * Notes:
 * We should probably add the ability to collapse widgets via an Input option
 */

export class WidgetComponent extends WidgetBaseComponent implements AfterViewInit {
    /**
     * Allow to use widget state enum in view
     */
    public WidgetStateEnum: typeof WidgetStateEnum = WidgetStateEnum;

    /**
     * Widget title
     */
    @Input() public title: string;

    /**
     * Widget ID
     */
    @Input() set id(id: string) {
        this._id = id;
    }

    /**
     * SSL Enabled
     */
    @Input() public sslEnabled: boolean = false;

    /**
     * Widget loading state
     */
    @Input() public loading: boolean;

    /**
     * Defines the state of the widget - IE. if Critical the border will turn red.
     */
    @Input() set state(state: WidgetStateEnum) {
        this._state = state;
    }

    /**
     * Force show content (bypass permissions).
     */
    @Input() public forceShowWidgetContent: boolean = false;

    /**
     * Widget has permissions to display data.
     */
    @Input() public hasPermissions: boolean = true;

    /**
     * Widget has data to display.
     */
    @Input() public hasData: boolean = true;

    /**
     * Notify other components of enabled content.
     */
    @Output() public showContent: EventEmitter<boolean> = new EventEmitter();

    /**
     * Store widget permissions.
     */
    public permissionsLookup: any = {};

    /**
     * Websocket closure flag.
     */
    public websocketClosed: boolean = false;

    /**
     * Check if content is enabled.
     */
    public contentEnabled: boolean = false;

    /**
     * Store id of widget.
     * @private
     */
    private _id: string;

    /**
     * Store widget state.
     * @private
     */
    private _state: WidgetStateEnum;

    constructor(
        private socketService: SocketService,
        utilsService: UtilsService,
    ) {
        super(utilsService);
    }

    get id(): string {
        return this._id;
    }

    get state(): any {
        return this._state;
    }

    public ngAfterViewInit(): void {
        this.enableContent();
        this.setupSocketListeners();
    }

    /**
     * Enable widget content.
     * @private
     */
    private enableContent(): void {
        this.contentEnabled = this.permissionsLookup.hasOwnProperty(this.id) ? this.permissionsLookup[this.id] : true;
        this.showContent.next(this.contentEnabled);
    }

    /**
     * Setup websocket listener.
     * @private
     */
    private setupSocketListeners(): void {
        this.socketService.socketClosedEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (response: any): void => {
                    this.websocketClosed = response;
                    this.loading = false;
                }
            });
    }
}
